/**
 * ChangeMarker — the visual envelope that wraps any paragraph-level change
 * block in either the side-by-side or unified view.
 *
 * Implements the colour + shape + texture triple-encoding from
 * `docs/design/comparison-visual-language.md` §1.1:
 *
 *   addition     → green   solid    `+`
 *   deletion     → red     solid    `−`
 *   modification → amber   dashed   `✏`
 *   move         → blue    dotted   `↕`
 *
 * Why we accept *both* colour and shape:
 *   - Colour alone fails for protan/deutan dichromacy (~6% of male users).
 *   - Bar texture (solid / dashed / dotted) is independent of colour.
 *   - A short symbol gives a third, language-agnostic discriminator.
 *
 * The component is purely presentational; selection / focus / accept-reject
 * states from §2 are layered on by Phases 3.4 and 3.6.
 *
 * Final-state override (M3 B5): when `decisionDisplay` is `'winner'` or
 * `'loser'`, the type-coded background/bar/text are replaced with a
 * decision-coded skin so the reader can tell at a glance which side will
 * become the final document. The type symbol stays in the corner so the
 * "this was originally a modification" context is preserved.
 */
import { memo, type ReactNode } from 'react';
import type { ChangeType } from '../../lib/veridoc-api';

export type DecisionDisplay = 'pending' | 'winner' | 'loser';

interface ChangeMarkerProps {
  changeType: ChangeType;
  children: ReactNode;
  /** Override the symbol shown at the top-right (e.g. unified view uses '−' on deletion strip). */
  symbolOverride?: string;
  /**
   * In unified view the deletion strip uses a tighter padding because it
   * sits just above the version-B replacement; pass `compact` to drop the
   * bottom margin.
   */
  compact?: boolean;
  /** Optional accessibility label — defaults to the localised type label. */
  ariaLabel?: string;
  /**
   * Final-state decoration after a review decision is recorded.
   *   `'winner'` → green skin, signals "this side becomes the final doc"
   *   `'loser'`  → muted gray skin + strikethrough, signals "abandoned"
   *   `'pending'` (default) keeps the original change-type colours.
   *
   * The owning view is responsible for mapping (status, cellRole) → display
   * (see `displayModeFor` in `DecidableChange.tsx`); ChangeMarker is purely
   * presentational and trusts whatever it receives.
   */
  decisionDisplay?: DecisionDisplay;
}

interface MarkerStyle {
  bgClass: string;
  borderColorClass: string;
  textClass: string;
  borderStyle: 'solid' | 'dashed' | 'dotted';
  symbol: string;
  ariaLabel: string;
}

const STYLES: Record<ChangeType, MarkerStyle> = {
  addition: {
    bgClass: 'bg-change-addition-bg',
    borderColorClass: 'border-change-addition-bar',
    textClass: 'text-change-addition-fg',
    borderStyle: 'solid',
    symbol: '+',
    ariaLabel: '新增内容',
  },
  deletion: {
    bgClass: 'bg-change-deletion-bg',
    borderColorClass: 'border-change-deletion-bar',
    textClass: 'text-change-deletion-fg',
    borderStyle: 'solid',
    symbol: '−', // minus sign — visually thicker than hyphen-minus
    ariaLabel: '删除内容',
  },
  modification: {
    bgClass: 'bg-change-modification-bg',
    borderColorClass: 'border-change-modification-bar',
    textClass: 'text-change-modification-fg',
    borderStyle: 'dashed',
    symbol: '~',
    ariaLabel: '修改内容',
  },
  move: {
    bgClass: 'bg-change-move-bg',
    borderColorClass: 'border-change-move-bar',
    textClass: 'text-change-move-fg',
    borderStyle: 'dotted',
    symbol: '↕', // up-down arrow
    ariaLabel: '移动内容',
  },
  format: {
    bgClass: 'bg-change-format-bg',
    borderColorClass: 'border-change-format-bar',
    textClass: 'text-change-format-fg',
    borderStyle: 'dashed',
    symbol: 'Aa', // indicates style/formatting change
    ariaLabel: '格式变更',
  },
};

const WINNER_OVERRIDE = {
  bgClass: 'bg-decision-winner-bg',
  borderColorClass: 'border-decision-winner-border',
  textClass: 'text-decision-winner-fg',
  borderStyle: 'solid' as const,
};

const LOSER_OVERRIDE = {
  bgClass: 'bg-decision-loser-bg',
  borderColorClass: 'border-decision-loser-border',
  // line-through propagates to inline descendants; the absolute-positioned
  // bar/symbol spans below opt out via `no-underline` so they stay legible.
  textClass: 'text-decision-loser-fg line-through decoration-decision-loser-decoration decoration-1',
  borderStyle: 'solid' as const,
};

/**
 * Wraps `children` in a coloured panel with a 4px left bar (whose stroke
 * style encodes the change type) and a circled symbol badge in the top
 * right corner.
 *
 * Tailwind cannot trivially render a "dashed" or "dotted" 4px-wide left
 * border because `border-l-4` only exposes one width across all sides; we
 * therefore implement the bar via an absolutely positioned ::before
 * pseudo-element rendered as inline style. Background + text colours stay
 * on Tailwind classes pulled from design tokens.
 */
export const ChangeMarker = memo(function ChangeMarker({
  changeType,
  children,
  symbolOverride,
  compact = false,
  ariaLabel,
  decisionDisplay = 'pending',
}: ChangeMarkerProps): React.JSX.Element {
  const baseStyle = STYLES[changeType];
  const style: MarkerStyle =
    decisionDisplay === 'winner'
      ? { ...baseStyle, ...WINNER_OVERRIDE }
      : decisionDisplay === 'loser'
        ? { ...baseStyle, ...LOSER_OVERRIDE }
        : baseStyle;
  const symbol = symbolOverride ?? style.symbol;
  const margin = compact ? '' : 'mb-4';

  return (
    <div
      className={`relative rounded-sm ${style.bgClass} ${style.textClass} ${margin} pl-4 pr-10 py-2`}
      role="group"
      aria-label={ariaLabel ?? style.ariaLabel}
      data-change-type={changeType}
      data-decision-display={decisionDisplay}
    >
      {/* Left bar (4px) — colour + texture comes from the change type. */}
      <span
        aria-hidden="true"
        className={`pointer-events-none absolute left-0 top-1 bottom-1 w-change-bar no-underline ${style.borderColorClass}`}
        style={{
          borderLeftWidth: '4px',
          borderLeftStyle: style.borderStyle,
        }}
      />
      {/* Top-right symbol badge — language-independent discriminator.
          `no-underline` keeps the symbol legible when the parent is in
          `loser` mode (which sets `line-through` for the body text). */}
      <span
        aria-hidden="true"
        className={`pointer-events-none absolute right-2 top-2 inline-flex h-5 min-w-5 items-center justify-center rounded-sm border no-underline ${style.borderColorClass} ${style.bgClass} px-1 text-xs font-semibold ${style.textClass}`}
      >
        {symbol}
      </span>
      {children}
    </div>
  );
});

export default ChangeMarker;
